/**
 * Deutsche Stromabgaben und Umlagen 2026
 * Alle Werte in ct/kWh (netto)
 * Quelle: Bundesnetzagentur, BDEW
 */

// Feste Umlagen (bundesweit einheitlich)
export const STROMSTEUER = 2.05;           // ct/kWh
export const OFFSHORE_UMLAGE = 0.656;      // ct/kWh
export const KWKG_UMLAGE = 0.275;          // ct/kWh
export const STROMNEV_UMLAGE = 0.643;      // ct/kWh
export const MESSSTELLENBETRIEB = 0.00;     // in Netzentgelt enthalten

// Konzessionsabgabe nach Gemeindegroesse (ct/kWh)
export const KONZESSIONSABGABE = {
  klein: 1.32,     // < 25.000 Einwohner
  mittel: 1.59,    // 25.000 - 100.000 Einwohner
  gross: 2.39,     // 100.000 - 500.000 Einwohner
  grossstadt: 2.39, // > 500.000 Einwohner
  sondervertrag: 0.11, // bei Sondervertragskunden
};

// PLZ-Prefix → Gemeindegroesse (vereinfacht, groesste Staedte)
const GROSSSTADT_PLZ = [
  '10', '12', '13', '14', // Berlin
  '20', '21', '22',       // Hamburg
  '80', '81', '85',       // Muenchen
  '50', '51',             // Koeln
  '60', '65',             // Frankfurt
  '70', '71',             // Stuttgart
  '40', '41',             // Duesseldorf
  '44', '45',             // Dortmund/Essen
  '04',                   // Leipzig
  '28',                   // Bremen
  '30', '31',             // Hannover
  '01',                   // Dresden
  '90',                   // Nuernberg
  '47',                   // Duisburg
  '46',                   // Oberhausen/Bochum
];

const GROSS_PLZ = [
  '53', // Bonn
  '48', // Muenster
  '76', // Karlsruhe
  '68', // Mannheim
  '86', // Augsburg
  '55', // Mainz/Wiesbaden
  '24', // Kiel
  '18', // Rostock
  '39', // Magdeburg
  '99', // Erfurt
  '06', // Halle
  '34', // Kassel
  '79', // Freiburg
  '38', // Braunschweig
  '54', // Trier
  '26', // Oldenburg
  '23', // Luebeck
  '09', // Chemnitz
  '07', // Jena/Gera
  '64', // Darmstadt
  '33', // Bielefeld/Paderborn
  '42', '43', // Wuppertal/Solingen
];

/**
 * Ermittelt die Konzessionsabgabe basierend auf PLZ
 */
function getKonzessionsabgabe(plz, isSondervertrag = false) {
  if (isSondervertrag) return KONZESSIONSABGABE.sondervertrag;

  const prefix = plz?.substring(0, 2) || '';

  if (GROSSSTADT_PLZ.includes(prefix)) return KONZESSIONSABGABE.grossstadt;
  if (GROSS_PLZ.includes(prefix)) return KONZESSIONSABGABE.gross;

  // Default: mittelgrosse Gemeinde
  return KONZESSIONSABGABE.mittel;
}

// Schwellwerte fuer die Abgrenzung Tarif- / Sondervertragskunde (KAV)
export const SONDERVERTRAG_MIN_KWH = 30000; // Jahresverbrauch
export const SONDERVERTRAG_MIN_KW = 30;     // Anschlussleistung

/**
 * Tarif- oder Sondervertragskunde?
 *
 * Nach der Konzessionsabgabenverordnung gelten Kunden als Sondervertragskunden,
 * wenn die Belieferung ueber Leistungsmessung erfolgt (RLM) oder Verbrauch und
 * Leistung ueber den Schwellwerten liegen. Fuer sie gilt die deutlich niedrigere
 * Konzessionsabgabe von 0,11 ct/kWh statt 1,32-2,39 ct/kWh.
 *
 * @param {{ verbrauch?: number, zaehlverfahren?: string, leistung?: number }} params
 * @returns {boolean}
 */
export function istSondervertragskunde({ verbrauch = 0, zaehlverfahren = 'SLP', leistung = 0 } = {}) {
  if (String(zaehlverfahren).toUpperCase() === 'RLM') return true; // RLM ist immer Sondervertrag
  if (Number(leistung) > SONDERVERTRAG_MIN_KW) return true;
  return Number(verbrauch) > SONDERVERTRAG_MIN_KWH;
}

/**
 * Berechnet die gesamten Abgaben und Umlagen
 * @param {string} plz - Postleitzahl
 * @param {string} spannungsebene - NS, MS, HS
 * @param {boolean} isSondervertrag - Sondervertragskunde (siehe istSondervertragskunde)
 * @returns {{ einzeln: Object, gesamt: number }}
 */
export function getAbgaben(plz, spannungsebene = 'NS', isSondervertrag = false) {
  const konzession = getKonzessionsabgabe(plz, isSondervertrag);

  // Bei Mittelspannung reduzierte Konzessionsabgabe
  const konzessionFaktor = spannungsebene === 'MS' ? 0.7 : spannungsebene === 'HS' ? 0.3 : 1.0;

  const einzeln = {
    stromsteuer: STROMSTEUER,
    offshore_umlage: OFFSHORE_UMLAGE,
    kwkg_umlage: KWKG_UMLAGE,
    stromnev_umlage: STROMNEV_UMLAGE,
    konzessionsabgabe: Math.round(konzession * konzessionFaktor * 10000) / 10000,
  };

  const gesamt = Object.values(einzeln).reduce((sum, v) => sum + v, 0);

  return {
    einzeln,
    gesamt: Math.round(gesamt * 10000) / 10000,
  };
}

/**
 * Liste der Branchen (wie ESOS24)
 */
export const BRANCHEN = [
  'Oeffentliche Verwaltungen, Dienstleistungen und Buerobetrieb',
  'Fitness und Wellness',
  'Fisch-, Fleisch- und Milchverarbeitung',
  'Einzelhandel und Maerkte',
  'Baeckereien',
  'Autohaeuser und KFZ-Gewerbe',
  'Handwerksbetriebe',
  'Hotels und Wohnheime',
  'Produzierendes Gewerbe',
  'Lagerhaltung und Logistik',
  'Landwirtschaft',
  'Gastronomie',
  'Gesundheitswesen und Pflege',
  'Bildungseinrichtungen',
  'Kultur und Freizeit',
  'IT und Rechenzentren',
  'Sonstige',
];

/**
 * Spannungsebenen
 */
export const SPANNUNGSEBENEN = [
  { value: 'NS', label: 'Niederspannung - Niederspannung' },
  { value: 'NS_MS', label: 'Niederspannung - Umspannung NS/MS' },
  { value: 'MS', label: 'Mittelspannung' },
  { value: 'MS_HS', label: 'Mittelspannung - Umspannung MS/HS' },
  { value: 'HS', label: 'Hochspannung' },
];

/**
 * Zaehlverfahren
 */
export const ZAEHLVERFAHREN = [
  { value: 'SLP', label: 'SLP (Standardlastprofil)', description: 'Bis 100.000 kWh/a' },
  { value: 'RLM', label: 'RLM (Registrierende Leistungsmessung)', description: 'Ab 100.000 kWh/a' },
];
